/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365 from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { IContext } from '@msdyn365-commerce/retail-proxy';
import { getZendeskDetailsActionAsync } from '../../../actions/ZendeskDataAction/DataActionExtension.g';
import { ZendeskEntityExtensionClass } from '../../../actions/ZendeskDataAction/DataServiceEntities.g';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
export {};
declare global {
    interface Window {
        clarity?: (...args: any[]) => void;
        zE?: any;
    }
}
/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;
    private _wasAuthenticated: boolean = false;
    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {

        super(props);
        this.menuNode = React.createRef();
    }

    
    

    private _addCookieDeclineButton(): void {
        const observer = new MutationObserver(() => {
            const modal = document.querySelector(".ms-cookie-compliance");
    
            if (modal && !modal.querySelector(".ms-cookie-compliance__decline-button")) {
                // Check if user already declined
                if (sessionStorage.getItem("cookiesDeclined") === "true") {
                    modal.setAttribute("style", "display:none");
                    if (window.clarity) {
                        window.clarity('consentv2', {
                            ad_Storage: "denied",
                            analytics_Storage: "denied"
                        });
                    }
                    observer.disconnect();
                    return;
                }
    
                // Create decline button
                const declineBtn = document.createElement("button");
                declineBtn.type = "button";
                declineBtn.innerText = "Decline";
                declineBtn.className = "ms-cookie-compliance__decline-button msc-btn";
                declineBtn.setAttribute("aria-label", "Decline cookies");
    
                // Close modal on click
                declineBtn.addEventListener("click", () => {
                    modal.setAttribute("style", "display:none");
    
                    // Save user choice for the session (until browser closed)
                    sessionStorage.setItem("cookiesDeclined", "true");
    
                    if (window.clarity) {
                        window.clarity('consentv2', {
                            ad_Storage: "denied",
                            analytics_Storage: "denied"
                        });
                    }
                });
    
                // Append decline button inside modal container
                const container = modal.querySelector(".ms-cookie-compliance__container");
                if (container) {
                    container.appendChild(declineBtn);
                }
    
                // stop observing once Decline button is added
                observer.disconnect();
            }
        });
    
        // Watch for modal injection
        observer.observe(document.body, { childList: true, subtree: true });
    }
    
    
    private _loadZendeskWidget = async (jwtTokenFromCSU: string, scriptFromCSU: string): Promise<void> => {
        try {
            const jwtToken = jwtTokenFromCSU;
    
            if (!jwtToken) {
                console.warn('Zendesk JWT not returned');
                return;
            }
    
            const script = document.createElement('script');
            script.src = scriptFromCSU;
            script.id = 'ze-snippet';
            script.async = true;
    
            script.onload = () => {
                if (window.zE) {
                    // New Zendesk Messaging API
                    try {
                        window.zE('messenger', 'logout');
                        window.zE('messenger', 'loginUser', {
                            jwt: jwtToken
                        });
    
                        console.log('Zendesk messaging widget loaded and authenticated.');
                    } catch (e) {
                        console.error('Zendesk messaging login failed:', e);
                    }
                }
            };
    
            document.head.appendChild(script);
        } catch (error) {
            console.error('Error loading Zendesk widget:', error);
        }
    };
    

    private _cleanCatalogUrlOnce() {
        
        const currentUrl = window.location.href;
    
        // TODO: Replace this with your own cleanup logic
        const updatedUrl = currentUrl.replace(/catalogid=\d+/i, '');
    
        if (updatedUrl !== currentUrl) {
            window.history.replaceState({}, '', updatedUrl);
        }
    
    }
    
    public componentDidMount(): void {
        this._addCookieDeclineButton();
        const { context } = this.props;
        const contextNew: IContext = {
            callerContext: context.actionContext
        };
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
        }
        if (MsDyn365.isBrowser && this.props.context.request.user?.isAuthenticated) {
            const signInName = this.props.context.request.user.signinName ?? '';
            const email = this.props.context.request.user.emailAddress ?? ''

            getZendeskDetailsActionAsync(contextNew, signInName, email)
            .then(response => {
                const zendesk = new ZendeskEntityExtensionClass(response);
                this._loadZendeskWidget(zendesk.JWTTOKEN, zendesk.SCRIPT)
            })
            .catch(error => {
                console.error('Error fetching tracking number:', error);
                this.setState({
                    trackingError: 'Unable to fetch shipment tracking number.',
                    trackingLoading: false
                });
            });
            //this._cleanCatalogUrlOnce();
         }
    }

    public render(): JSX.Element | null {

        const isAuthenticated = this.props.context.request.user?.isAuthenticated ?? false;

        // Detect login transition
        if (!this._wasAuthenticated && isAuthenticated) {
            this._cleanCatalogUrlOnce();
        }
    
        this._wasAuthenticated = isAuthenticated;
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { HeaderTag, HeaderContainer, HeaderTopBarContainer, Divider, MobileMenuContainer, MobileMenuBodyContainer, MobileMenuLinksContainer
        } = this.props;
        const shouldShowNav =  this.props.config.hideNavIfNotAuthenticated ?  this.props.context.request.user.isAuthenticated : true;

        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        <div className='desktop-navicon-container' ref={this.menuNode}>
                            {shouldShowNav ? this._renderReactFragment(this.props.menuBar) : <div />}
                        </div>
                        {shouldShowNav ? 
                            <div className='mobile-navicon-container'>
                                {this.props.navIcon}
                            </div>
                        : <div />}
                        {this.props.logo}
                        {this._renderMobileLogo()}
                        {shouldShowNav ? this._renderReactFragment(this.props.search) : <div className='ms-search blank'></div>}
                        {this.props.preferredStore}
                        {this._renderReactFragment(this.props.siteOptions)}
                        {this._renderAccountBlock(this.props)}
                        <Node {...Divider} />
                        {shouldShowNav ? this.props.cartIcon : <div className='ms-cart-icon'></div>}
                    </Node>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            { this.props.MobileMenuHeader }
                            {this._renderReactFragment(this.props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                { this.props.accountLinks ? this.props.accountLinks.map(link => link) : false }
                                { this.props.siteOptions }
                                { this.props.wishListIconMobile }
                                { this.props.signInLink }
                                { this.props.signOutLink }
                            </Node>
                        </Node>
                    </Node>
                </Node>
            </Module>
        );
    }

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        return (
            <Logo {
                ...{ link: config.logoLink,
                    image: config.mobileLogoImage ?? {},
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName }} />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { AccountInfoDropdownParentContainer, AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signOutLink,
            signInLink,
            accountLinks
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            { accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false }
                            {signOutLink}
                        </Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items) ? items.map((slot: React.ReactNode) => {
                    return (<>
                        {slot}
                    </>);
                }) : null}
            </>
        );
    };
}

export default HeaderView;
